/**
 * AdminDashboard ("לוח בקרה") — route /admin. Admin alerts: payment, email, backup, DB and system
 * errors the server records in admin_alerts (services/service.adminAlerts.js).
 *
 * Tabs:
 *   התראות  stats + filters + the alerts, as a list (each alert, row opens message / stack / details)
 *           or grouped by fingerprint (same error → one row with counts; row opens its history).
 *           Actions: mark one alert resolved, resolve a whole group, send a test alert, refresh.
 *   מגמות   last 7 days: alerts per day, most common errors, alerts per hour of day.
 *   מערכת   email scheduler (running? inside sending hours? + manual cycle), email health, backups.
 *
 * Data (unchanged): getAdminAlerts(100) · getAdminAlertStats · getGroupedAlerts(50) ·
 * getAlertsByFingerprint(fp, 20) on first expand · getAlertTrends(7) when the tab opens ·
 * resolveAlert · resolveByFingerprint · sendTestAlert · triggerEmailScheduler. Messages via showAlert.
 *
 * Changes beyond the look (2026-10, see the commit):
 *   - The old "מערכת אימיילים" tab read fields the server never sends (intervalMinutes, health), so
 *     it always said "בעיה" / 0%. The "מערכת" tab now shows what /api/email/scheduler/status really
 *     returns (isRunning, withinBusinessHours) plus GET /api/email/health (getEmailHealth — the
 *     same call the email dashboard uses).
 *   - The old "סטטוס מערכת" tab was hard-coded ("פעיל", "מחובר", "כל יום ב-02:00") — removed.
 *     There is no backup status endpoint; backups show their last failure from the alerts instead.
 *   - Grouped filter "פתוח"/"טופל": the server sends counts as strings, so "0" === 0 never matched
 *     and resolved groups stayed under "פתוח". Counts are parsed now.
 *   - Severity / type show in Hebrew (the raw value stays in a tooltip).
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  ReloadOutlined,
  BellOutlined,
  CheckOutlined,
  CopyOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import * as actionsCreators from '../../actions/index.js';
import {
  Page,
  PageHeader,
  Section,
  DataTable,
  EmptyState,
  StatusBadge,
  StatCard,
  Metric,
  StatusDot,
  Button,
  Input,
  Select,
  Segmented,
  Tabs,
  Tooltip,
  Spin,
  useBreakpoint,
} from '../../ui';
import classes from './AdminDashboard.css';

// ---- labels ------------------------------------------------------------------------
const SEVERITY = {
  critical: { label: 'קריטי', color: '#DC2626' },
  high: { label: 'גבוה', color: '#EA580C' },
  medium: { label: 'בינוני', color: '#D97706' },
  low: { label: 'נמוך', color: '#2563EB' },
};
const TYPE = {
  PAYMENT_FAILURE: 'תשלום',
  EMAIL_FAILURE: 'אימייל',
  BACKUP_FAILURE: 'גיבוי',
  DATABASE_ERROR: 'מסד נתונים',
  SYSTEM_ERROR: 'מערכת',
};
const OPEN_COLOR = '#D97706';
const RESOLVED_COLOR = '#16A34A';

const severityBadge = (severity) => {
  const meta = SEVERITY[severity] || { label: severity, color: '#A1A1AA' };
  return <StatusBadge color={meta.color}>{meta.label}</StatusBadge>;
};
const typeChip = (type) => (
  <Tooltip title={<span dir="ltr">{type}</span>} mouseEnterDelay={0.4}>
    <span className={classes.typeChip}>{TYPE[type] || type}</span>
  </Tooltip>
);
const statusBadge = (resolved) =>
  resolved ? <StatusBadge color={RESOLVED_COLOR}>טופל</StatusBadge> : <StatusBadge color={OPEN_COLOR}>פתוח</StatusBadge>;

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const num = (v) => parseInt(v, 10) || 0; // grouped/trend counts arrive as strings

// Single-series bars: one accent color, thin rounded bars, hover tooltip per bar, quiet axis.
const Bars = ({ items, emptyText, ariaLabel }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.some((i) => i.value > 0)) return <p className={classes.noData}>{emptyText}</p>;
  return (
    <div className={classes.bars} role="img" aria-label={ariaLabel}>
      {items.map((i) => (
        <Tooltip key={i.key} title={i.tip} mouseEnterDelay={0}>
          <div className={classes.barCol}>
            {/* only the tallest bar carries its number; the rest are on hover */}
            <span className={classes.barValue}>{i.value === max ? i.value : ''}</span>
            <div className={classes.barTrack}>
              <div className={classes.bar} style={{ height: i.value ? `${Math.max(4, (i.value / max) * 100)}%` : 0 }} />
            </div>
            <span className={classes.barLabel}>{i.label}</span>
            {i.sub ? <span className={classes.barSub}>{i.sub}</span> : null}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

const AdminDashboard = (props) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [groupedAlerts, setGroupedAlerts] = useState([]);
  const [trends, setTrends] = useState(null);
  const [alertStats, setAlertStats] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null); // UI: real health for the "מערכת" tab
  const [loading, setLoading] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupDetails, setGroupDetails] = useState({});
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grouped'
  const [triggering, setTriggering] = useState(false); // UI only
  const screens = useBreakpoint();

  useEffect(() => {
    loadAlerts();
    loadAlertStats();
    loadGroupedAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'system') {
      loadEmailStatus();
    } else if (activeTab === 'trends') {
      loadTrends();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const data = await props.getAdminAlerts(100);
      setAlerts(data);
    } catch (error) {
      props.showAlert('שגיאה בטעינת התראות');
    }
    setLoading(false);
  };

  const loadGroupedAlerts = async () => {
    try {
      const data = await props.getGroupedAlerts(50);
      setGroupedAlerts(data);
    } catch (error) {
      console.error('Error loading grouped alerts:', error);
    }
  };

  const loadTrends = async () => {
    try {
      const data = await props.getAlertTrends(7);
      setTrends(data);
    } catch (error) {
      console.error('Error loading trends:', error);
    }
  };

  const loadAlertStats = async () => {
    try {
      const data = await props.getAdminAlertStats();
      setAlertStats(data);
    } catch (error) {
      console.error('Error loading alert stats:', error);
    }
  };

  const loadEmailStatus = async () => {
    try {
      const data = await props.getEmailSchedulerStatus();
      setEmailStatus(data);
    } catch (error) {
      console.error('Error loading email status:', error);
    }
    try {
      setEmailHealth(await props.getEmailHealth());
    } catch (error) {
      console.error('Error loading email health:', error);
    }
  };

  const reloadAll = () => {
    loadAlerts();
    loadAlertStats();
    loadGroupedAlerts();
  };

  const handleTestAlert = async () => {
    try {
      await props.sendTestAlert();
      props.showAlert('התראת בדיקה נשלחה בהצלחה');
      reloadAll();
    } catch (error) {
      props.showAlert('שגיאה בשליחת התראת בדיקה');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await props.resolveAlert(alertId);
      props.showAlert('ההתראה סומנה כטופלה');
      reloadAll();
    } catch (error) {
      props.showAlert('שגיאה בעדכון ההתראה');
    }
  };

  const handleResolveByFingerprint = async (fingerprint) => {
    try {
      const result = await props.resolveByFingerprint(fingerprint);
      props.showAlert('נפתרו ' + result.resolvedCount + ' התראות');
      // the open group's history is cached — drop it so it reloads with the new status
      setGroupDetails((prev) => {
        const next = { ...prev };
        delete next[fingerprint];
        return next;
      });
      if (expandedGroup === fingerprint) setExpandedGroup(null);
      reloadAll();
    } catch (error) {
      props.showAlert('שגיאה בעדכון ההתראות');
    }
  };

  const handleExpandGroup = async (fingerprint) => {
    if (expandedGroup === fingerprint) {
      setExpandedGroup(null);
      return;
    }
    setExpandedGroup(fingerprint);
    if (!groupDetails[fingerprint]) {
      try {
        const data = await props.getAlertsByFingerprint(fingerprint, 20);
        setGroupDetails((prev) => ({ ...prev, [fingerprint]: data }));
      } catch (error) {
        console.error('Error loading group details:', error);
      }
    }
  };

  const handleTriggerEmailScheduler = async () => {
    setTriggering(true);
    try {
      await props.triggerEmailScheduler();
      props.showAlert('מתזמן האימיילים הופעל');
      loadEmailStatus();
    } catch (error) {
      props.showAlert('שגיאה בהפעלת המתזמן');
    }
    setTriggering(false);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(
      () => props.showAlert('הועתק'),
      () => {}
    );
  };

  // ---- filters (unchanged logic; grouped counts parsed) ----
  const searchLower = searchTerm.toLowerCase();
  const filteredAlerts = alerts.filter((alert) => {
    if (filterSeverity !== 'all' && alert.severity !== filterSeverity) return false;
    if (filterType !== 'all' && alert.alertType !== filterType) return false;
    if (filterStatus === 'open' && alert.resolvedAt) return false;
    if (filterStatus === 'resolved' && !alert.resolvedAt) return false;
    if (searchTerm) {
      const titleMatch = alert.title && alert.title.toLowerCase().indexOf(searchLower) !== -1;
      const messageMatch = alert.message && alert.message.toLowerCase().indexOf(searchLower) !== -1;
      const sourceMatch = alert.source && alert.source.toLowerCase().indexOf(searchLower) !== -1;
      if (!titleMatch && !messageMatch && !sourceMatch) return false;
    }
    return true;
  });

  const filteredGroupedAlerts = groupedAlerts.filter((group) => {
    if (filterSeverity !== 'all' && group.severity !== filterSeverity) return false;
    if (filterType !== 'all' && group.alert_type !== filterType) return false;
    if (filterStatus === 'open' && num(group.unresolved_count) === 0) return false;
    if (filterStatus === 'resolved' && num(group.unresolved_count) > 0) return false;
    if (searchTerm) {
      const titleMatch = group.title && group.title.toLowerCase().indexOf(searchLower) !== -1;
      const sourceMatch = group.source && group.source.toLowerCase().indexOf(searchLower) !== -1;
      if (!titleMatch && !sourceMatch) return false;
    }
    return true;
  });

  const filtersActive = filterSeverity !== 'all' || filterType !== 'all' || filterStatus !== 'all' || !!searchTerm;
  const clearFilters = () => {
    setFilterSeverity('all');
    setFilterType('all');
    setFilterStatus('all');
    setSearchTerm('');
  };

  // ---- alerts tab ----------------------------------------------------------------
  const renderStats = () => {
    const bySeverity = (alertStats && alertStats.bySeverity) || {};
    const v = (n) => (alertStats ? n || 0 : '–');
    return (
      <div className={classes.statsGrid}>
        <div className={classes.stat}>
          <Metric size="lg" value={v(alertStats && alertStats.today)} label="התראות היום" />
        </div>
        <div className={classes.stat}>
          <Metric size="lg" value={v(alertStats && alertStats.thisWeek)} label="התראות השבוע" />
        </div>
        <div className={classes.stat}>
          <Metric size="lg" value={v(bySeverity.critical)} label="קריטיות" color={bySeverity.critical ? SEVERITY.critical.color : undefined} />
        </div>
        <div className={classes.stat}>
          <Metric size="lg" value={v(bySeverity.high)} label="גבוהות" color={bySeverity.high ? SEVERITY.high.color : undefined} />
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div className={classes.filters}>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="חיפוש בכותרת, הודעה או מקור…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className={classes.search}
      />
      <Select
        value={filterSeverity}
        onChange={setFilterSeverity}
        className={classes.filterSelect}
        options={[{ value: 'all', label: 'כל הרמות' }, ...Object.keys(SEVERITY).map((k) => ({ value: k, label: SEVERITY[k].label }))]}
      />
      <Select
        value={filterType}
        onChange={setFilterType}
        className={classes.filterSelect}
        options={[{ value: 'all', label: 'כל הסוגים' }, ...Object.keys(TYPE).map((k) => ({ value: k, label: TYPE[k] }))]}
      />
      <Segmented
        value={filterStatus}
        onChange={setFilterStatus}
        options={[
          { value: 'all', label: 'הכל' },
          { value: 'open', label: 'פתוח' },
          { value: 'resolved', label: 'טופל' },
        ]}
      />
      {filtersActive ? (
        <Button type="link" size="small" onClick={clearFilters}>
          נקה
        </Button>
      ) : null}
    </div>
  );

  const renderAlertDetails = (alert) => {
    const details = alert.details || null;
    const rest = details ? Object.fromEntries(Object.entries(details).filter((e) => e[0] !== 'stack')) : {};
    return (
      <div className={classes.details}>
        {alert.message ? <p className={classes.detailMessage} dir="auto">{alert.message}</p> : null}
        {details && details.stack ? (
          <div className={classes.codeBlock}>
            <div className={classes.codeHead}>
              <span>Stack trace</span>
              <Tooltip title="העתק">
                <Button size="small" type="text" icon={<CopyOutlined />} aria-label="העתק stack" onClick={() => copyText(details.stack)} />
              </Tooltip>
            </div>
            <pre className={classes.pre}>{details.stack}</pre>
          </div>
        ) : null}
        {Object.keys(rest).length > 0 ? (
          <div className={classes.codeBlock}>
            <div className={classes.codeHead}>
              <span>פרטים</span>
              <Tooltip title="העתק">
                <Button size="small" type="text" icon={<CopyOutlined />} aria-label="העתק פרטים" onClick={() => copyText(JSON.stringify(rest, null, 2))} />
              </Tooltip>
            </div>
            <pre className={classes.pre}>{JSON.stringify(rest, null, 2)}</pre>
          </div>
        ) : null}
        {!alert.message && !details ? <span className={classes.muted}>אין פרטים נוספים</span> : null}
      </div>
    );
  };

  const listColumns = [
    {
      key: 'created_at',
      title: 'זמן',
      width: 150,
      responsive: ['md'],
      render: (_, a) => <span className={classes.date}>{formatDate(a.created_at)}</span>,
    },
    { key: 'severity', title: 'רמה', width: 100, responsive: ['sm'], render: (_, a) => severityBadge(a.severity) },
    { key: 'type', title: 'סוג', width: 110, responsive: ['sm'], render: (_, a) => typeChip(a.alertType) },
    {
      key: 'title',
      title: 'כותרת',
      render: (_, a) => (
        <div className={classes.titleCell}>
          <span className={classes.title} dir="auto">{a.title}</span>
          {a.source ? <span className={classes.source} dir="auto">{a.source}</span> : null}
          {screens.md === false ? <span className={classes.source}>{formatDate(a.created_at)}</span> : null}
          {/* phones: level + status move here (their columns are hidden) */}
          {screens.sm === false ? (
            <span className={classes.phoneMeta}>
              {severityBadge(a.severity)}
              {statusBadge(!!a.resolvedAt)}
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'status', title: 'סטטוס', width: 90, responsive: ['sm'], render: (_, a) => statusBadge(!!a.resolvedAt) },
    {
      key: 'actions',
      title: '',
      width: screens.md === false ? 56 : 130,
      align: 'left',
      render: (_, a) =>
        a.resolvedAt ? null : screens.md === false ? (
          <Tooltip title="סמן כטופל">
            <Button size="small" icon={<CheckOutlined />} aria-label="סמן כטופל" onClick={() => handleResolveAlert(a.id)} />
          </Tooltip>
        ) : (
          <Button size="small" icon={<CheckOutlined />} onClick={() => handleResolveAlert(a.id)}>
            סמן כטופל
          </Button>
        ),
    },
  ];

  const renderGroupHistory = (group) => {
    const details = groupDetails[group.fingerprint];
    if (!details) {
      return (
        <div className={classes.details}>
          <Spin size="small" /> <span className={classes.muted}>טוען…</span>
        </div>
      );
    }
    return (
      <div className={classes.details}>
        <div className={classes.historyTitle}>היסטוריית מופעים</div>
        <ul className={classes.history}>
          {details.map((alert) => (
            <li key={alert.id}>
              <span className={classes.date}>{formatDate(alert.created_at)}</span>
              <span className={classes.historyMessage} dir="auto">{alert.message}</span>
              {statusBadge(!!alert.resolvedAt)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const groupColumns = [
    {
      key: 'title',
      title: 'כותרת',
      render: (_, g) => (
        <div className={classes.titleCell}>
          <span className={classes.title} dir="auto">{g.title}</span>
          {g.source ? <span className={classes.source} dir="auto">{g.source}</span> : null}
          {screens.sm === false ? (
            <span className={classes.phoneMeta}>
              {severityBadge(g.severity)}
              <span className={classes.countOpen}>
                {num(g.occurrence_count)} מופעים{num(g.unresolved_count) > 0 ? ` · ${num(g.unresolved_count)} פתוחות` : ''}
              </span>
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'severity', title: 'רמה', width: 100, responsive: ['sm'], render: (_, g) => severityBadge(g.severity) },
    { key: 'type', title: 'סוג', width: 110, responsive: ['sm'], render: (_, g) => typeChip(g.alert_type) },
    {
      key: 'count',
      title: 'מופעים',
      width: 120,
      responsive: ['sm'],
      render: (_, g) => (
        <span className={classes.counts}>
          <span className={classes.countTotal}>{num(g.occurrence_count)}</span>
          {num(g.unresolved_count) > 0 ? <span className={classes.countOpen}>{num(g.unresolved_count)} פתוחות</span> : null}
        </span>
      ),
    },
    {
      key: 'last',
      title: 'אחרון',
      width: 150,
      responsive: ['md'],
      render: (_, g) => <span className={classes.date}>{formatDate(g.last_occurrence)}</span>,
    },
    {
      key: 'actions',
      title: '',
      width: screens.md === false ? 56 : 140,
      align: 'left',
      render: (_, g) =>
        num(g.unresolved_count) > 0 ? (
          screens.md === false ? (
            <Tooltip title={`פתור הכל (${g.unresolved_count})`}>
              <Button size="small" icon={<CheckOutlined />} aria-label="פתור הכל" onClick={() => handleResolveByFingerprint(g.fingerprint)} />
            </Tooltip>
          ) : (
            <Button size="small" icon={<CheckOutlined />} onClick={() => handleResolveByFingerprint(g.fingerprint)}>
              פתור הכל ({g.unresolved_count})
            </Button>
          )
        ) : null,
    },
  ];

  const emptyAlerts = (
    <EmptyState
      title={filtersActive ? 'אין התראות שמתאימות לסינון' : 'אין התראות'}
      action={
        filtersActive ? (
          <Button onClick={clearFilters}>נקה סינון</Button>
        ) : null
      }
    />
  );

  const renderAlertsTab = () => (
    <div className={classes.tab}>
      {renderStats()}
      <Section
        title="התראות אחרונות"
        padded={false}
        extra={
          <Segmented
            size="small"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: 'list', label: 'רשימה' },
              { value: 'grouped', label: 'מקובץ' },
            ]}
          />
        }
      >
        {renderFilters()}
        {viewMode === 'grouped' ? (
          <DataTable
            key="grouped"
            data={filteredGroupedAlerts}
            rowKey="fingerprint"
            fit
            loading={loading}
            columns={groupColumns}
            empty={emptyAlerts}
            onRowClick={(g) => handleExpandGroup(g.fingerprint)}
            expandable={{
              expandedRowKeys: expandedGroup ? [expandedGroup] : [],
              onExpand: (_open, g) => handleExpandGroup(g.fingerprint),
              expandedRowRender: renderGroupHistory,
            }}
          />
        ) : (
          <DataTable
            key="list"
            data={filteredAlerts}
            fit
            loading={loading}
            columns={listColumns}
            empty={emptyAlerts}
            onRowClick={(a) => setExpandedAlert(expandedAlert === a.id ? null : a.id)}
            expandable={{
              expandedRowKeys: expandedAlert ? [expandedAlert] : [],
              onExpand: (_open, a) => setExpandedAlert(expandedAlert === a.id ? null : a.id),
              expandedRowRender: renderAlertDetails,
            }}
          />
        )}
      </Section>
    </div>
  );

  // ---- trends tab ----------------------------------------------------------------
  const renderTrendsTab = () => {
    if (!trends) {
      return (
        <div className={classes.loadingBox}>
          <Spin />
        </div>
      );
    }
    const daily = (trends.dailyCounts || []).map((day) => {
      const date = new Date(day.date);
      const dayDate = date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
      return {
        key: day.date,
        value: num(day.total),
        label: date.toLocaleDateString('he-IL', { weekday: 'short' }),
        sub: dayDate,
        tip: `${dayDate}: ${num(day.total)} התראות` + (num(day.critical) ? ` · ${num(day.critical)} קריטיות` : ''),
      };
    });
    const hourly = Array.from({ length: 24 }, (_, hour) => {
      const h = (trends.hourlyDistribution || []).find((x) => num(x.hour) === hour);
      const count = h ? num(h.count) : 0;
      return { key: hour, value: count, label: hour % 3 === 0 ? String(hour) : '', tip: `${String(hour).padStart(2, '0')}:00 — ${count} התראות` };
    });
    const top = (trends.topErrors || []).slice(0, 5);
    const topMax = Math.max(1, ...top.map((e) => num(e.count)));

    return (
      <div className={classes.trendsGrid}>
        <StatCard title="התראות ביום · 7 ימים אחרונים">
          <Bars items={daily} emptyText="אין התראות בשבוע האחרון" ariaLabel="התראות ביום" />
        </StatCard>
        <StatCard title="שגיאות נפוצות">
          {top.length === 0 ? (
            <p className={classes.noData}>אין נתונים</p>
          ) : (
            <ol className={classes.topList}>
              {top.map((error, index) => (
                <li key={index}>
                  <div className={classes.topHead}>
                    <span className={classes.topTitle} dir="auto">{error.title}</span>
                    <span className={classes.topCount}>{num(error.count)}</span>
                  </div>
                  <div className={classes.topMeta}>{TYPE[error.alert_type] || error.alert_type}</div>
                  <div className={classes.topTrack}>
                    <span className={classes.topFill} style={{ width: `${(num(error.count) / topMax) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </StatCard>
        <StatCard title="לפי שעה ביום" className={classes.wide}>
          <Bars items={hourly} emptyText="אין נתונים" ariaLabel="התראות לפי שעה ביום" />
        </StatCard>
      </div>
    );
  };

  // ---- system tab ----------------------------------------------------------------
  const renderSystemTab = () => {
    const running = emailStatus && emailStatus.isRunning;
    const lastBackupFailure = alerts.find((a) => a.alertType === 'BACKUP_FAILURE');
    return (
      <div className={classes.systemGrid}>
        <StatCard
          title="מתזמן אימיילים"
          extra={
            <Button size="small" icon={<ThunderboltOutlined />} loading={triggering} onClick={handleTriggerEmailScheduler}>
              הפעל מחזור ידני
            </Button>
          }
        >
          {emailStatus ? (
            <div className={classes.facts}>
              <StatusDot color={running ? RESOLVED_COLOR : '#DC2626'}>{running ? 'פעיל' : 'לא פעיל'}</StatusDot>
              {running ? (
                <span className={classes.fact}>
                  {emailStatus.withinBusinessHours ? 'בתוך שעות השליחה — מיילים יוצאים עכשיו' : 'מחוץ לשעות השליחה — מיילים ממתינים'}
                </span>
              ) : null}
            </div>
          ) : (
            <Spin size="small" />
          )}
        </StatCard>

        <StatCard title="בריאות שליחת מיילים">
          {emailHealth ? (
            <div className={classes.facts}>
              <StatusDot color={emailHealth.healthy ? RESOLVED_COLOR : '#DC2626'}>{emailHealth.healthy ? 'תקין' : 'בעיה'}</StatusDot>
              <span className={classes.fact}>
                {Number(emailHealth.failureRate || 0).toFixed(1)}% כשלונות ב-{emailHealth.windowMinutes || 30} הדקות האחרונות
              </span>
              {emailHealth.threshold ? <span className={classes.fact}>השליחה נעצרת אוטומטית מעל {emailHealth.threshold}%</span> : null}
            </div>
          ) : (
            <Spin size="small" />
          )}
        </StatCard>

        <StatCard title="גיבויים">
          <div className={classes.facts}>
            {lastBackupFailure ? (
              <>
                <StatusDot color={lastBackupFailure.resolvedAt ? '#A1A1AA' : '#DC2626'}>
                  {lastBackupFailure.resolvedAt ? 'תקלה אחרונה טופלה' : 'תקלה פתוחה'}
                </StatusDot>
                <span className={classes.fact}>
                  {formatDate(lastBackupFailure.created_at)} — {lastBackupFailure.title}
                </span>
              </>
            ) : (
              <>
                <StatusDot color={RESOLVED_COLOR}>אין תקלות גיבוי</StatusDot>
                <span className={classes.fact}>ב-100 ההתראות האחרונות. גיבוי שנכשל יופיע כאן וכהתראת "גיבוי".</span>
              </>
            )}
          </div>
        </StatCard>
      </div>
    );
  };

  return (
    <Page>
      <PageHeader
        title="לוח בקרה"
        subtitle="התראות מערכת: תשלומים, מיילים, גיבויים ושגיאות"
        actions={
          <>
            <Tooltip title="רענן">
              <Button icon={<ReloadOutlined />} aria-label="רענן" loading={loading} onClick={reloadAll} />
            </Tooltip>
            <Button icon={<BellOutlined />} onClick={handleTestAlert}>
              {screens.sm === false ? 'בדיקה' : 'שלח התראת בדיקה'}
            </Button>
          </>
        }
      />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'alerts', label: 'התראות', children: renderAlertsTab() },
          { key: 'trends', label: 'מגמות', children: renderTrendsTab() },
          { key: 'system', label: 'מערכת', children: renderSystemTab() },
        ]}
      />
    </Page>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getAdminAlerts: actionsCreators.getAdminAlerts,
      getAdminAlertStats: actionsCreators.getAdminAlertStats,
      getGroupedAlerts: actionsCreators.getGroupedAlerts,
      getAlertTrends: actionsCreators.getAlertTrends,
      getAlertsByFingerprint: actionsCreators.getAlertsByFingerprint,
      sendTestAlert: actionsCreators.sendTestAlert,
      resolveAlert: actionsCreators.resolveAlert,
      resolveByFingerprint: actionsCreators.resolveByFingerprint,
      updateAlertNotes: actionsCreators.updateAlertNotes,
      getEmailSchedulerStatus: actionsCreators.getEmailSchedulerStatus,
      getEmailHealth: actionsCreators.getEmailHealth,
      triggerEmailScheduler: actionsCreators.triggerEmailScheduler,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AdminDashboard);
